import type * as React from 'react';
import { cn } from '@/lib/utils';

type ProgressTone = 'primary' | 'success' | 'warning' | 'danger';

const FILL: Readonly<Record<ProgressTone, string>> = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

/**
 * Progress bar (DESIGN_SYSTEM §4, T-093): 8 px sunken track, rounded fill in a tone. Exposes
 * `role="progressbar"` with min/max/now; `label` is its accessible name and `valueText` (e.g.
 * "16 of 25 steps done") replaces the bare percentage for screen readers.
 */
function ProgressBar({
  value,
  max = 100,
  label,
  valueText,
  tone = 'primary',
  className,
  ...props
}: Omit<React.ComponentProps<'div'>, 'children' | 'role'> & {
  value: number;
  max?: number;
  label: string;
  valueText?: string;
  tone?: ProgressTone;
}) {
  const safeMax = max > 0 ? max : 100;
  const clamped = Math.min(safeMax, Math.max(0, value));
  const percent = (clamped / safeMax) * 100;
  return (
    <div
      data-slot="progress-bar"
      data-tone={tone}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      aria-valuenow={clamped}
      aria-valuetext={valueText}
      className={cn('h-2 w-full overflow-hidden rounded-full bg-surface-muted', className)}
      {...props}
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-200 ease-out', FILL[tone])}
        style={{ width: `${String(percent)}%` }}
      />
    </div>
  );
}

export { ProgressBar, type ProgressTone };
